const ROUTES = {
  CREATE_SPACE: "/your-space",
  LISTING: (id: number | string, urlPrefix?: string) =>
    `${urlPrefix ? `/${urlPrefix}` : ""}/pages/listings/${id}`,
  HOST_HUB: "/host-hub",
};

/**
 * Central registry of route path prefixes that require an authenticated
 * session. This is the single source of truth for the two-layer auth gate:
 *
 * 1. The Next.js proxy (`src/proxy.ts`) reads it to block token-less
 *    requests at the edge before the page renders.
 * 2. Protected pages wrap their content in `AuthGuard` /`useRequireAuth`, which
 *    do the authoritative check against the resolved profile.
 *
 * To protect a new route: add its public path here and wrap the page content in
 * `AuthGuard`. No other change is required.
 *
 * Paths are the *public* (locale-stripped) pathnames, e.g. "/profile", because
 * middleware runs before the next.config `rewrites` that add the locale segment.
 * Use "*" for a dynamic path segment, e.g. "/vendors/profile/*\/edit" matches
 * "/vendors/profile/<id>/edit" but not "/vendors/profile/<id>".
 */
const PROTECTED_ROUTES = [
  "/profile",
  "/updated-terms",
  "/vendors/profile/*/edit",
] as const;

/**
 * Routes that use a reduced page shell: logo-only nav (`showLogoOnly`) and no
 * footer.
 */
const MINIMAL_SHELL_ROUTES = ["/updated-terms"] as const;

/**
 * Paths whose navbar starts transparent over a hero, locale segment already stripped.
 * Matched on the server too: waiting for a registered trigger meant the first byte of
 * HTML carried the opaque navbar, and the element changed type on hydration.
 */
const TRANSPARENT_NAVBAR_PATHS = new Set(["/"]);

const LOCALE_URL_PREFIXES = ["au", "ca", "de", "es", "fr", "uk", "us"] as const;

/**
 * Strips a leading segment only when it is a *recognized* locale prefix.
 */
const LOCALE_PREFIX_REGEX = new RegExp(
  `^/(?:${LOCALE_URL_PREFIXES.join("|")})(?=/|$)`,
  "i",
);

export {
  LOCALE_PREFIX_REGEX,
  LOCALE_URL_PREFIXES,
  MINIMAL_SHELL_ROUTES,
  PROTECTED_ROUTES,
  ROUTES,
  TRANSPARENT_NAVBAR_PATHS,
};
